import CustomTooltip from '@gui/components/generic/CustomTooltip';
import { Panels } from '@gui/constants/Panels';
import { ControllerManager } from '@gui/controller/ControllerManager';
import usePersistentStore, { useDesignSchemesBucket, useGlobalBucket } from '@gui/store/persistent/persistentStore';
import { PolygonDrawer } from '@gui/components/DrawModule/PolygonDrawer';
import FVector from '@data/geometry/FVector';
import { Box, Button, Checkbox, MenuItem, Select, Slider, Typography } from '@mui/material';
import { DrawModule } from '@rendering/DrawingModule/DrawingModule';
import { BorderMode, BorderSource, DeepPartial, DesignSchemeSetType, FloorBasedDesignState } from '@visualizer/types';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { DesignSchemeController } from '../DesignSchemeView';
import Grid from 'Geodata/grid';
import { getGrid } from 'store/LoadingStore';
import { FloorBasedFacadesView } from './FloorBasedFacadesView';
import {
    CollapsibleGroupLabel,
    formatOneDecimal,
    formatTwoDecimals,
    sliderInteractionProps,
    sliderRowSx,
    sliderSx,
    sliderUnitSx,
    tinyLabelSx,
    tinySelectSx,
} from './FloorBasedView.shared';

export interface FloorBasedGuiProps {
    setId: string;
    footprintId: string;
    schemeId: string;
}

export const FloorBasedView: React.FC<FloorBasedGuiProps> = ({ setId, footprintId, schemeId }) => {
    const designSchemesController = ControllerManager.getInstance().getDesignSchemeController();

    // Zustand Variables:
    const designSchemes = useDesignSchemesBucket((s) => s);
    const setState = designSchemes.sets?.[setId];
    const scheme = designSchemes.sets?.[setId]?.footprints?.[footprintId]?.designSchemes?.[schemeId];
    const floorBasedState = scheme.floorBased;
    const setType = setState?.type;
    const isFullSite = setType === DesignSchemeSetType.FULL_SITE;

    // Defaults:
    const defaults = designSchemes.defaults.floorBased;
    const active = floorBasedState?.active ?? defaults.active;

    // Border Settings:
    const borderMode = floorBasedState?.borderState.mode ?? defaults.borderState.mode;
    const singleBorderSource = floorBasedState?.borderState.single ?? defaults.borderState.single;
    const internalBorderSource = floorBasedState?.borderState.internal ?? defaults.borderState.internal;
    const externalBorderSource = floorBasedState?.borderState.external ?? defaults.borderState.external;
    const boxAlpha = floorBasedState?.boxAlpha?.value ?? defaults.boxAlpha.value;

    // Project Sketch Border:
    const projectSketchIndex = floorBasedState?.borderState.projectSketchIndex ?? defaults.borderState.projectSketchIndex ?? 0;

    // Hand Drawn Border(s):
    const handDrawnBorder = floorBasedState?.borderState.handDrawnBorder ?? defaults.borderState.handDrawnBorder ?? [];
    const handDrawnExternalBorder =
        floorBasedState?.borderState.handDrawnExternalBorder ?? defaults.borderState.handDrawnExternalBorder ?? [];
    const handDrawnInternalBorder =
        floorBasedState?.borderState.handDrawnInternalBorder ?? defaults.borderState.handDrawnInternalBorder ?? [];
    const toPlainVectors = (points: { x: number; y: number; z: number }[]) => points.map((point) => new FVector(point.x, point.y, point.z));
    const handDrawnBorderPoints = toPlainVectors(handDrawnBorder);
    const handDrawnExternalBorderPoints = toPlainVectors(handDrawnExternalBorder);
    const handDrawnInternalBorderPoints = toPlainVectors(handDrawnInternalBorder);

    // Box Border:
    const usesBoxBorder =
        (borderMode === BorderMode.SINGLE && singleBorderSource === BorderSource.BOX) ||
        (borderMode === BorderMode.DUAL && (externalBorderSource === BorderSource.BOX || internalBorderSource === BorderSource.BOX));
    const isLinkedToMainLayer = floorBasedState?.linkToMainLayer ?? defaults.linkToMainLayer ?? true;
    const availableBorderSources = [
        { value: BorderSource.MAIN_LAYER, label: 'Analysis Area' },
        { value: BorderSource.FOOTPRINT, label: 'Footprint' },
        ...(isFullSite ? [{ value: BorderSource.BUILDABLE_BORDER, label: 'Buildable Border' }] : []),
        ...(isFullSite ? [{ value: BorderSource.PROJECT_SKETCH, label: 'Project Sketch' }] : []),
        { value: BorderSource.HAND_DRAWN, label: 'Hand Drawn' },
        { value: BorderSource.BOX, label: 'Box' },
    ];
    const projectSketchContourCount = getGrid().getActiveGeodata().dataObject.Buildings?.getProjectSketchContourCount() ?? 0;

    // Floors & Concaveness:
    const groundFloorHeight = floorBasedState?.groundFloorHeight?.value ?? defaults.groundFloorHeight.value;
    const floorHeight = floorBasedState?.floorHeight?.value ?? defaults.floorHeight.value;
    const floorThickness = floorBasedState?.thickness?.value ?? defaults.thickness.value;
    const buildingHeight = floorBasedState?.buildingHeight?.value ?? defaults.buildingHeight.value;
    const voxelFloorsEnabled = floorBasedState?.voxelFloors ?? defaults.voxelFloors;
    const concaveEnabled = floorBasedState?.concave ?? defaults.concave;
    const concaveAlpha = floorBasedState?.concaveAlpha?.value ?? defaults.concaveAlpha.value;

    // Facades:
    const hideFacadesEnabled = floorBasedState?.facadeState.hide ?? defaults.facadeState.hide;
    const verticalHeight = floorBasedState?.facadeState.verticalHeight?.value ?? defaults.facadeState.verticalHeight.value;

    // React State:
    const [maxBuildingHeight, setMaxBuildingHeight] = useState<number | null>(null);
    const [sliderLabelWidth, setSliderLabelWidth] = useState<number>(0);
    const [activeHandDrawnEditor, setActiveHandDrawnEditor] = useState<'single' | 'external' | 'internal' | null>(null);
    const [sectionsOpen, setSectionsOpen] = useState({
        borders: false,
        floors: false,
    });
    const sliderSectionRef = useRef<HTMLDivElement | null>(null);
    const handDrawnSingleDrawModuleRef = useRef<DrawModule | null>(null);
    const handDrawnExternalDrawModuleRef = useRef<DrawModule | null>(null);
    const handDrawnInternalDrawModuleRef = useRef<DrawModule | null>(null);
    const context = useGlobalBucket((s) => s.context);
    const setPanel = usePersistentStore((s) => s.setPanel);
    const previousShowGridRef = useRef<boolean | null>(null);
    const wasSchemeVisibleAtEditStartRef = useRef<boolean | null>(null);

    const isUsingHandDrawnSource =
        (borderMode === BorderMode.SINGLE && singleBorderSource === BorderSource.HAND_DRAWN) ||
        (borderMode === BorderMode.DUAL &&
            (externalBorderSource === BorderSource.HAND_DRAWN || internalBorderSource === BorderSource.HAND_DRAWN));

    // Handlers:
    const updateFloorBased = (partial: DeepPartial<FloorBasedDesignState>) => {
        designSchemesController.updateDesignScheme(setId, footprintId, schemeId, {
            floorBased: partial,
        });
    };

    // Collapsable Sections:
    const toggleSection = (section: 'borders' | 'floors') => {
        setSectionsOpen((prev) => ({
            ...prev,
            [section]: !prev[section],
        }));
    };

    // Makes sure that if any Hand Drawn border source is selected, the corresponding DrawModule is available to be used:
    const ensureHandDrawnDrawModule = (editor: 'single' | 'external' | 'internal') => {
        // If Draw Module exists for the selected border, return it:
        const currentRef =
            editor === 'single'
                ? handDrawnSingleDrawModuleRef
                : editor === 'external'
                  ? handDrawnExternalDrawModuleRef
                  : handDrawnInternalDrawModuleRef;
        if (currentRef.current) return currentRef.current;

        // If Draw Module doesn't exist for the selected border, create it:
        currentRef.current = new DrawModule((newPts, failed) => {
            if (failed) return;
            const nextPoints = newPts.map((point) => new FVector(point.x, point.y, point.z));
            updateFloorBased({
                borderState: {
                    ...(editor === 'single'
                        ? { handDrawnBorder: nextPoints }
                        : editor === 'external'
                          ? { handDrawnExternalBorder: nextPoints }
                          : { handDrawnInternalBorder: nextPoints }),
                },
            });
            setActiveHandDrawnEditor(null);
        }, Grid.getPoints());

        return currentRef.current;
    };

    // Handles layer linking/unlinking:
    const handleMainLayerLinkToggle = (checked: boolean) => {
        // 1) Link off. Use Footprints for borders when link is unchecked:
        if (!checked) {
            // 1.1) SINGLE mode: if current source is Main Layer, fallback to Footprint:
            if (borderMode === BorderMode.SINGLE) {
                updateFloorBased({
                    linkToMainLayer: false,
                    borderState: {
                        single: singleBorderSource === BorderSource.MAIN_LAYER ? BorderSource.FOOTPRINT : singleBorderSource,
                    },
                });
                return;
            }

            // 1.2) DUAL mode: do the same, independently for each border:
            updateFloorBased({
                linkToMainLayer: false,
                borderState: {
                    external: externalBorderSource === BorderSource.MAIN_LAYER ? BorderSource.FOOTPRINT : externalBorderSource,
                    internal: internalBorderSource === BorderSource.MAIN_LAYER ? BorderSource.FOOTPRINT : internalBorderSource,
                },
            });
            return;
        }

        // 2) Link on. Enable layer linking (no forcing of borders to footprint).
        updateFloorBased({ linkToMainLayer: true });
    };

    // =====================================
    // USE EFFECTS
    // =====================================

    // Ensures FloorBased defaults follow the current grid resolution:
    useEffect(() => {
        if (Grid.gridRes <= defaults.floorHeight.minValue) return;

        designSchemesController.updateDefaults({
            floorBased: {
                floorHeight: {
                    minValue: Grid.gridRes,
                },
                facadeState: {
                    verticalHeight: {
                        minValue: Grid.gridRes,
                    },
                },
            },
        });
    }, [designSchemes.defaults.floorBased?.facadeState.verticalHeight?.maxValue]);

    // Clamp FloorBased values to grid resolution when defaults are too high (Used for big gridRes values):
    useEffect(() => {
        if (!floorBasedState || Grid.gridRes <= defaults.floorHeight.minValue) return;

        updateFloorBased({
            floorHeight: { value: Grid.gridRes },
            facadeState: {
                verticalHeight: { value: Grid.gridRes },
            },
        });
    }, [designSchemes.defaults.floorBased.floorHeight.minValue]);

    // Read the dynamic building-height slider max cached by FloorBased runtime:
    useEffect(() => {
        const ds = designSchemesController.getDesignScheme(schemeId);
        const maxHeight = (ds as { getMainLayermaxHeight?: () => number | null } | undefined)?.getMainLayermaxHeight?.() ?? null;
        if (maxHeight == null) {
            setMaxBuildingHeight(null);
            return;
        }
        setMaxBuildingHeight(maxHeight + Grid.gridRes / 2 + floorThickness);
    }, [schemeId, floorThickness, floorBasedState?.buildingHeight?.value]);

    useLayoutEffect(() => {
        const root = sliderSectionRef.current;
        if (!root) return;

        const labels = Array.from(root.querySelectorAll<HTMLElement>('[data-slider-label="true"]')).filter(
            (label) => label.offsetParent !== null
        );
        const nextWidth = Math.ceil(Math.max(...labels.map((label) => label.getBoundingClientRect().width), 0));

        if (nextWidth !== sliderLabelWidth) {
            setSliderLabelWidth(nextWidth);
        }
    }, [active, borderMode, concaveEnabled, voxelFloorsEnabled, sectionsOpen.borders, sectionsOpen.floors, sliderLabelWidth]);

    useEffect(() => {
        if (projectSketchContourCount < 1) return;
        if (projectSketchIndex >= 0 && projectSketchIndex < projectSketchContourCount) return;
        updateFloorBased({
            borderState: {
                projectSketchIndex: 0,
            },
        });
    }, [projectSketchContourCount, projectSketchIndex]);

    // =====================================
    // HAND DRAWN BORDERS EFFECTS
    // =====================================

    // Activate/deactivate Hand Drawn editor based on selected border sources:
    useEffect(() => {
        if (!isUsingHandDrawnSource) {
            setActiveHandDrawnEditor(null);
            return;
        }
        setActiveHandDrawnEditor((current) => current ?? (borderMode === BorderMode.SINGLE ? 'single' : null));
    }, [isUsingHandDrawnSource]);

    // Ensures the corresponding DrawModule is available when activating the Hand Drawn editor:
    useEffect(() => {
        if (!activeHandDrawnEditor) return;
        ensureHandDrawnDrawModule(activeHandDrawnEditor);
    }, [activeHandDrawnEditor]);

    // Hides/Shows the DesignScheme and the SensorPoints when DrawModule is Active/Inactive:
    useEffect(() => {
        const isEditingHandDrawn = !!activeHandDrawnEditor;
        let unsubscribeGenerationComplete: (() => void) | undefined;

        // 1) Entering Hand Drawn edit mode:
        if (isEditingHandDrawn) {
            // 1.1) Save current UI visibility state so we can restore it when editing finishes:
            if (previousShowGridRef.current === null) previousShowGridRef.current = context.showGrid;
            if (wasSchemeVisibleAtEditStartRef.current === null) wasSchemeVisibleAtEditStartRef.current = scheme?.visibility ?? true;

            // 1.2) Hide sensor points & DS mesh while editing the border:
            if (context.showGrid) setPanel({ showGrid: false }, Panels.CONTEXT);

            const runtimeScheme = designSchemesController.getDesignScheme(schemeId);
            if (runtimeScheme && scheme?.visibility) {
                runtimeScheme.hide();

                // 1.3) Add Listener: If DS mesh regenerates during edit, force-hide again:
                unsubscribeGenerationComplete = runtimeScheme.getRenderer().onGenerationComplete(() => {
                    if (activeHandDrawnEditor) runtimeScheme.hide();
                });
            }

            // 1.4) Remove Listener on unmount:
            return () => {
                unsubscribeGenerationComplete?.();
            };
        }

        // 2) Exiting Hand Drawn edit mode:
        // 2.1) Restore DS mesh visibility only if it was visible when edit started:
        if (wasSchemeVisibleAtEditStartRef.current === true && scheme?.visibility) {
            const runtimeScheme = designSchemesController.getDesignScheme(schemeId);
            runtimeScheme?.draw();
        }

        // 2.2) Restore sensor points only if they were visible when edit started:
        if (previousShowGridRef.current === true) {
            setPanel({ showGrid: true }, Panels.CONTEXT);
        }

        // 2.3) Clear Saved UI Visibility state:
        wasSchemeVisibleAtEditStartRef.current = null;
        previousShowGridRef.current = null;

        // 2.4) Defensive cleanup for any pending generation subscription:
        return () => {
            unsubscribeGenerationComplete?.();
        };
    }, [activeHandDrawnEditor, context.showGrid, scheme?.visibility, schemeId]);

    // Cleanup of Hand Drawn Borders DrawModules on unmount:
    useEffect(() => {
        return () => {
            handDrawnSingleDrawModuleRef.current?.dispose(true);
            handDrawnExternalDrawModuleRef.current?.dispose(true);
            handDrawnInternalDrawModuleRef.current?.dispose(true);
            handDrawnSingleDrawModuleRef.current = null;
            handDrawnExternalDrawModuleRef.current = null;
            handDrawnInternalDrawModuleRef.current = null;
        };
    }, []);

    // =====================================
    // ACTUAL HTML
    // =====================================

    const sliderLabelSx = {
        whiteSpace: 'nowrap',
        flex: `0 0 ${sliderLabelWidth || 1}px`,
    };
    const borderLabelSx = { ...tinyLabelSx, flex: '0 0 72px' };

    return (
        <Box>
            {/** Description */}
            <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                <CustomTooltip
                    mode="icon"
                    side="right"
                    title={
                        <Typography
                            sx={{
                                fontSize: '10.5px',
                                lineHeight: 1.4,
                                color: 'text.secondary',
                            }}
                        >
                            Provides a fast and intuitive way to understand how a potential building responds to site-specific environmental
                            conditions. It is driven by architectural attributes such as border typologies, floor structure, and facade
                            settings, which are combined through geometric rules to visualize and iteratively refine how the building takes
                            shape within its context.
                        </Typography>
                    }
                >
                    <Typography variant="subtitle1" align="center" sx={{ textAlign: 'center' }}>
                        Floor-Based Scheme
                    </Typography>
                </CustomTooltip>
            </Box>

            {active && (
                <Box mt={1} ref={sliderSectionRef}>
                    <CollapsibleGroupLabel label="Borders" open={sectionsOpen.borders} onToggle={() => toggleSection('borders')} />
                    {sectionsOpen.borders && (
                        <>
                            <DesignSchemeController
                                schemeId={schemeId}
                                footprintId={footprintId}
                                setId={setId}
                                connectedLayerLabel="Border Layer"
                                showLinkCheckbox
                                linkChecked={isLinkedToMainLayer}
                                onLinkChange={handleMainLayerLinkToggle}
                            />
                            {/* Border Type & Sources */}
                            <Box mt={1}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                    }}
                                >
                                    <Typography sx={borderLabelSx}>Border Type</Typography>

                                    <Select
                                        fullWidth
                                        size="small"
                                        value={borderMode}
                                        onChange={(e) => updateFloorBased({ borderState: { mode: e.target.value } })}
                                        sx={tinySelectSx}
                                    >
                                        <MenuItem value={BorderMode.SINGLE}>Single Border</MenuItem>
                                        <MenuItem value={BorderMode.DUAL}>Dual Border</MenuItem>
                                    </Select>
                                </Box>
                            </Box>

                            {borderMode === BorderMode.SINGLE && (
                                <Box mt={1}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                        }}
                                    >
                                        <Typography sx={borderLabelSx}>Border Source</Typography>

                                        <Select
                                            fullWidth
                                            size="small"
                                            value={singleBorderSource}
                                            onChange={(e) => {
                                                const nextSource = Number(e.target.value) as BorderSource;
                                                updateFloorBased({ borderState: { single: nextSource } });

                                                // If switching to Hand Drawn source, activate the correct drawer:
                                                if (nextSource === BorderSource.HAND_DRAWN) setActiveHandDrawnEditor('single');
                                            }}
                                            sx={tinySelectSx}
                                        >
                                            {availableBorderSources.map((source) => (
                                                <MenuItem
                                                    key={source.value}
                                                    value={source.value}
                                                    disabled={!isLinkedToMainLayer && source.value === BorderSource.MAIN_LAYER}
                                                >
                                                    {source.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </Box>
                                </Box>
                            )}
                            {borderMode === BorderMode.SINGLE &&
                                singleBorderSource === BorderSource.PROJECT_SKETCH &&
                                projectSketchContourCount > 1 && (
                                    <Box mt={1}>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                            }}
                                        >
                                            <Typography sx={borderLabelSx}>Sketch</Typography>

                                            <Select
                                                fullWidth
                                                size="small"
                                                value={Math.min(projectSketchIndex, projectSketchContourCount - 1)}
                                                onChange={(e) =>
                                                    updateFloorBased({
                                                        borderState: { projectSketchIndex: Number(e.target.value) },
                                                    })
                                                }
                                                sx={tinySelectSx}
                                            >
                                                {Array.from({ length: projectSketchContourCount }, (_, index) => (
                                                    <MenuItem key={index} value={index}>
                                                        {`Project Sketch ${index + 1}`}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </Box>
                                    </Box>
                                )}

                            {borderMode === BorderMode.DUAL && (
                                <>
                                    {/* External Border */}
                                    <Box mt={1}>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                            }}
                                        >
                                            <Typography sx={borderLabelSx}>External Border</Typography>

                                            <Select
                                                fullWidth
                                                size="small"
                                                value={externalBorderSource}
                                                onChange={(e) => {
                                                    const nextSource = Number(e.target.value) as BorderSource;
                                                    updateFloorBased({
                                                        borderState: { external: nextSource },
                                                    });

                                                    // If switching to Hand Drawn source, activate the correct drawer:
                                                    if (nextSource === BorderSource.HAND_DRAWN) setActiveHandDrawnEditor('external');
                                                }}
                                                sx={tinySelectSx}
                                            >
                                                {availableBorderSources.map((source) => (
                                                    <MenuItem
                                                        key={source.value}
                                                        value={source.value}
                                                        disabled={!isLinkedToMainLayer && source.value === BorderSource.MAIN_LAYER}
                                                    >
                                                        {source.label}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </Box>
                                    </Box>

                                    {/* Internal Border */}
                                    <Box mt={1}>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                            }}
                                        >
                                            <Typography sx={borderLabelSx}>Internal Border</Typography>

                                            <Select
                                                fullWidth
                                                size="small"
                                                value={internalBorderSource}
                                                onChange={(e) => {
                                                    const nextSource = Number(e.target.value) as BorderSource;
                                                    updateFloorBased({
                                                        borderState: { internal: nextSource },
                                                    });

                                                    // If switching to Hand Drawn source, activate the correct drawer:
                                                    if (nextSource === BorderSource.HAND_DRAWN) setActiveHandDrawnEditor('internal');
                                                }}
                                                sx={tinySelectSx}
                                            >
                                                {availableBorderSources.map((source) => (
                                                    <MenuItem
                                                        key={source.value}
                                                        value={source.value}
                                                        disabled={!isLinkedToMainLayer && source.value === BorderSource.MAIN_LAYER}
                                                    >
                                                        {source.label}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </Box>
                                    </Box>
                                </>
                            )}

                            {/** Polygon Drawer(s) and Edit button(s) */}
                            {isUsingHandDrawnSource && (
                                <Box mt={1} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    {borderMode === BorderMode.SINGLE && (
                                        <Button size="small" variant="outlined" onClick={() => setActiveHandDrawnEditor('single')}>
                                            {activeHandDrawnEditor === 'single'
                                                ? 'Currently editing Hand Drawn Border'
                                                : 'Edit Hand Drawn Border'}
                                        </Button>
                                    )}
                                    {borderMode === BorderMode.DUAL && externalBorderSource === BorderSource.HAND_DRAWN && (
                                        <Button size="small" variant="outlined" onClick={() => setActiveHandDrawnEditor('external')}>
                                            {activeHandDrawnEditor === 'external'
                                                ? 'Currently editing External Border'
                                                : 'Edit External Hand Drawn Border'}
                                        </Button>
                                    )}
                                    {borderMode === BorderMode.DUAL && internalBorderSource === BorderSource.HAND_DRAWN && (
                                        <Button size="small" variant="outlined" onClick={() => setActiveHandDrawnEditor('internal')}>
                                            {activeHandDrawnEditor === 'internal'
                                                ? 'Currently editing Internal Border'
                                                : 'Edit Internal Hand Drawn Border'}
                                        </Button>
                                    )}
                                    {activeHandDrawnEditor === 'single' && handDrawnSingleDrawModuleRef.current && (
                                        <PolygonDrawer
                                            isActive
                                            initialPoints={handDrawnBorderPoints}
                                            drawModule={handDrawnSingleDrawModuleRef.current}
                                        />
                                    )}
                                    {activeHandDrawnEditor === 'external' && handDrawnExternalDrawModuleRef.current && (
                                        <PolygonDrawer
                                            isActive
                                            initialPoints={handDrawnExternalBorderPoints}
                                            drawModule={handDrawnExternalDrawModuleRef.current}
                                        />
                                    )}
                                    {activeHandDrawnEditor === 'internal' && handDrawnInternalDrawModuleRef.current && (
                                        <PolygonDrawer
                                            isActive
                                            initialPoints={handDrawnInternalBorderPoints}
                                            drawModule={handDrawnInternalDrawModuleRef.current}
                                        />
                                    )}
                                </Box>
                            )}

                            {usesBoxBorder && (
                                <Box sx={{ ...sliderRowSx, mt: 1 }}>
                                    <Typography variant="body2" sx={sliderLabelSx} data-slider-label="true">
                                        Box Alpha
                                    </Typography>
                                    <Slider
                                        {...sliderInteractionProps}
                                        className="panel-slider"
                                        size="small"
                                        value={boxAlpha}
                                        min={defaults.boxAlpha.minValue}
                                        max={defaults.boxAlpha.maxValue}
                                        step={1}
                                        valueLabelDisplay="on"
                                        valueLabelFormat={formatOneDecimal}
                                        onChange={(_, value) => updateFloorBased({ boxAlpha: { value } })}
                                        sx={sliderSx}
                                    />
                                    <Typography variant="body2" sx={sliderUnitSx}>
                                        [deg]
                                    </Typography>
                                </Box>
                            )}
                        </>
                    )}

                    <CollapsibleGroupLabel label="Floors" open={sectionsOpen.floors} onToggle={() => toggleSection('floors')} />
                    {sectionsOpen.floors && (
                        <>
                            {/* Floor Informations: */}
                            {!voxelFloorsEnabled && (
                                <Box sx={sliderRowSx}>
                                    <Typography variant="body2" sx={sliderLabelSx} data-slider-label="true">
                                        Ground Floor H
                                    </Typography>

                                    <Slider
                                        {...sliderInteractionProps}
                                        data-testid="ds-ground-floor-height-slider"
                                        className="panel-slider"
                                        size="small"
                                        value={groundFloorHeight}
                                        min={defaults.groundFloorHeight.minValue}
                                        max={defaults.groundFloorHeight.maxValue}
                                        step={0.1}
                                        valueLabelDisplay="on"
                                        valueLabelFormat={formatOneDecimal}
                                        onChange={(_, value) => updateFloorBased({ groundFloorHeight: { value } })}
                                        sx={sliderSx}
                                    />
                                    <Typography variant="body2" sx={sliderUnitSx}>
                                        [m]
                                    </Typography>
                                </Box>
                            )}

                            {!voxelFloorsEnabled && (
                                <Box sx={sliderRowSx}>
                                    <Typography variant="body2" sx={sliderLabelSx} data-slider-label="true">
                                        Floor H
                                    </Typography>

                                    {/* The update to verticalHeight is to make it ALWAYS proportionate to the changes of floorHeight. */}
                                    <Slider
                                        {...sliderInteractionProps}
                                        data-testid="ds-floors-height-slider"
                                        className="panel-slider"
                                        size="small"
                                        value={floorHeight}
                                        min={defaults.floorHeight.minValue}
                                        max={defaults.floorHeight.maxValue}
                                        step={0.1}
                                        valueLabelDisplay="on"
                                        valueLabelFormat={formatOneDecimal}
                                        onChange={(_, value) => {
                                            updateFloorBased({
                                                facadeState: {
                                                    verticalHeight: { value: verticalHeight * (value / floorHeight) },
                                                },
                                            });
                                            updateFloorBased({ floorHeight: { value } });
                                        }}
                                        sx={sliderSx}
                                    />
                                    <Typography variant="body2" sx={sliderUnitSx}>
                                        [m]
                                    </Typography>
                                </Box>
                            )}

                            <Box sx={sliderRowSx}>
                                <Typography variant="body2" sx={sliderLabelSx} data-slider-label="true">
                                    Slab Thickness
                                </Typography>

                                <Slider
                                    {...sliderInteractionProps}
                                    data-testid="ds-floor-thickness-slider"
                                    className="panel-slider"
                                    size="small"
                                    value={floorThickness}
                                    min={defaults.thickness.minValue}
                                    max={defaults.thickness.maxValue}
                                    step={0.01}
                                    valueLabelDisplay="on"
                                    valueLabelFormat={formatTwoDecimals}
                                    onChange={(_, value) => updateFloorBased({ thickness: { value } })}
                                    sx={sliderSx}
                                />
                                <Typography variant="body2" sx={sliderUnitSx}>
                                    [m]
                                </Typography>
                            </Box>

                            <Box sx={sliderRowSx}>
                                <Typography variant="body2" sx={sliderLabelSx} data-slider-label="true">
                                    Building H
                                </Typography>

                                <Slider
                                    {...sliderInteractionProps}
                                    data-testid="ds-building-height-slider"
                                    className="panel-slider"
                                    size="small"
                                    value={buildingHeight}
                                    min={defaults.buildingHeight.minValue}
                                    max={maxBuildingHeight ?? defaults.buildingHeight.maxValue}
                                    step={0.1}
                                    valueLabelDisplay="on"
                                    valueLabelFormat={formatOneDecimal}
                                    onChange={(_, value) => updateFloorBased({ buildingHeight: { value } })}
                                    sx={sliderSx}
                                />
                                <Typography variant="body2" sx={sliderUnitSx}>
                                    [m]
                                </Typography>
                            </Box>

                            {/**Use Levels, Concave & Facade checkboxes */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1, mb: 1, flexWrap: 'wrap' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Checkbox
                                        data-testid="ds-voxel-floors-checkbox"
                                        size="small"
                                        checked={voxelFloorsEnabled}
                                        onChange={(e) => updateFloorBased({ voxelFloors: e.target.checked })}
                                        sx={{ padding: 0, '& .MuiSvgIcon-root': { fontSize: '14px' } }}
                                    />
                                    <Typography sx={{ fontSize: '10px', whiteSpace: 'nowrap' }}>Use Levels</Typography>
                                </Box>

                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Checkbox
                                        data-testid="ds-concave-checkbox"
                                        size="small"
                                        checked={concaveEnabled}
                                        onChange={(e) => updateFloorBased({ concave: e.target.checked })}
                                        sx={{ padding: 0, ml: 0.5, '& .MuiSvgIcon-root': { fontSize: '14px' } }}
                                    />
                                    <Typography sx={{ fontSize: '10px', whiteSpace: 'nowrap' }}>Concave</Typography>
                                </Box>

                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Checkbox
                                        data-testid="ds-hide-facades-checkbox"
                                        size="small"
                                        checked={hideFacadesEnabled}
                                        onChange={(e) => updateFloorBased({ facadeState: { hide: e.target.checked } })}
                                        sx={{ padding: 0, ml: 0.5, '& .MuiSvgIcon-root': { fontSize: '14px' } }}
                                    />
                                    <Typography sx={{ fontSize: '10px', whiteSpace: 'nowrap' }}>Hide Facades</Typography>
                                </Box>
                            </Box>

                            {/** Concaveness Slider */}
                            {concaveEnabled && (
                                <Box sx={sliderRowSx}>
                                    <Typography variant="body2" sx={sliderLabelSx} data-slider-label="true">
                                        Concaveness
                                    </Typography>

                                    <Slider
                                        {...sliderInteractionProps}
                                        data-testid="ds-concaveness-slider"
                                        className="panel-slider"
                                        size="small"
                                        value={concaveAlpha}
                                        min={defaults.concaveAlpha.minValue}
                                        max={defaults.concaveAlpha.maxValue}
                                        step={0.1}
                                        valueLabelDisplay="on"
                                        valueLabelFormat={formatOneDecimal}
                                        onChange={(_, value) => updateFloorBased({ concaveAlpha: { value } })}
                                        sx={sliderSx}
                                    />
                                    <Typography variant="body2" sx={sliderUnitSx}>
                                        [a]
                                    </Typography>
                                </Box>
                            )}
                        </>
                    )}

                    {/** ===================================== */}
                    {/** FACADES */}
                    {/** ===================================== */}

                    <FloorBasedFacadesView
                        setId={setId}
                        footprintId={footprintId}
                        schemeId={schemeId}
                        floorHeight={floorHeight}
                        floorThickness={floorThickness}
                        hideFacadesEnabled={hideFacadesEnabled}
                        updateFloorBased={updateFloorBased}
                    />
                </Box>
            )}
        </Box>
    );
};
